import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm } from 'react-hook-form';
import { ContractorPortalUserFormData } from '@/hooks/useContractorPortalUsers';

interface AddPortalUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ContractorPortalUserFormData) => Promise<void>;
}

export function AddPortalUserDialog({ open, onOpenChange, onSubmit }: AddPortalUserDialogProps) {
  const { register, handleSubmit, formState: { isSubmitting }, setValue, watch, reset } = useForm<ContractorPortalUserFormData>({
    defaultValues: {
      is_primary_contact: false,
      can_upload_documents: true,
      can_view_prequalification_status: true,
    }
  });

  const handleFormSubmit = async (data: ContractorPortalUserFormData) => {
    await onSubmit(data);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Portal User</DialogTitle>
          <DialogDescription>
            Create a new contractor portal user account. An invitation will be sent after creation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                {...register('email')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="e.g., Project Manager"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                {...register('first_name')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                {...register('last_name')}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                {...register('phone')}
              />
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t">
            <Label>Permissions</Label>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_primary_contact"
                checked={watch('is_primary_contact')}
                onCheckedChange={(checked) => setValue('is_primary_contact', checked as boolean)}
              />
              <Label htmlFor="is_primary_contact" className="font-normal cursor-pointer">
                Primary Contact
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="can_upload_documents"
                checked={watch('can_upload_documents')}
                onCheckedChange={(checked) => setValue('can_upload_documents', checked as boolean)}
              />
              <Label htmlFor="can_upload_documents" className="font-normal cursor-pointer">
                Can Upload Documents
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="can_view_prequalification_status"
                checked={watch('can_view_prequalification_status')}
                onCheckedChange={(checked) => setValue('can_view_prequalification_status', checked as boolean)}
              />
              <Label htmlFor="can_view_prequalification_status" className="font-normal cursor-pointer">
                Can View Prequalification Status
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create & Send Invitation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}